import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';

import { annotateNarrative } from '@/features/patient-record/api/textMining';
import {
  dismissalKey,
  useDismissedSuggestionsStore,
} from '@/features/patient-record/stores/dismissedSuggestionsStore';
import { cn } from '@/lib/utils';

import type { NarrativeAnnotation } from '@/features/patient-record/api/textMining';

import { PhiHintLabel } from './PhiHintLabel';

interface NarrativeWithSuggestionsProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly guid: string;
  readonly onAcceptHpo: (annotation: NarrativeAnnotation) => void;
  readonly onAcceptOmim: (annotation: NarrativeAnnotation) => void;
}

const DEBOUNCE_MS = 800;

/**
 * Narrative editor with an inline-suggestion side panel. Detected
 * HPO / OMIM term mentions surface as Accept / Dismiss cards beside
 * the textarea (PTV2-1051 #5).
 *
 * Phase 1 stub: the BE Text Mining endpoint is TBD, so the annotate
 * call is gated by `narrativeNlp` upstream and returns `[]` when the
 * endpoint isn't deployed yet. Accept routes the annotation back to
 * the parent so HPO mentions create phenotype rows and OMIM mentions
 * create diagnoses (creation path landing on the BE separately).
 *
 * Inline term highlighting inside the textarea is on the spec but not
 * built here — overlay rendering requires either ContentEditable or a
 * positioned span layer over the textarea. Tracked as a follow-up.
 */
export function NarrativeWithSuggestions({
  id,
  label,
  placeholder,
  value,
  onChange,
  guid,
  onAcceptHpo,
  onAcceptOmim,
}: NarrativeWithSuggestionsProps): ReactElement {
  const [debounced, setDebounced] = useState(value);
  const dismissedKeys = useDismissedSuggestionsStore((s) => s.keys);
  const dismiss = useDismissedSuggestionsStore((s) => s.dismiss);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(value);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
    };
  }, [value]);

  const annotateQuery = useQuery({
    queryKey: ['narrative-nlp', 'annotate', debounced],
    queryFn: ({ signal }) => annotateNarrative({ text: debounced }, { signal }),
    enabled: debounced.trim().length > 0,
    staleTime: 30_000,
  });

  const annotations = annotateQuery.data ?? [];
  const visibleAnnotations = annotations.filter(
    (a) => dismissedKeys[dismissalKey(guid, a.vocabulary, a.termId, a.snippet)] !== true,
  );

  return (
    <div className="space-y-1.5">
      <PhiHintLabel htmlFor={id} label={label} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_18rem] gap-3">
        <textarea
          id={id}
          value={value}
          placeholder={placeholder}
          rows={3}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed resize-y min-h-[80px]"
        />
        <SuggestionPanel
          annotations={visibleAnnotations}
          loading={annotateQuery.isFetching && debounced.trim().length > 0}
          onAccept={(a) => {
            if (a.vocabulary === 'hpo') onAcceptHpo(a);
            else onAcceptOmim(a);
          }}
          onDismiss={(a) => {
            dismiss(dismissalKey(guid, a.vocabulary, a.termId, a.snippet));
          }}
        />
      </div>
    </div>
  );
}

interface SuggestionPanelProps {
  readonly annotations: readonly NarrativeAnnotation[];
  readonly loading: boolean;
  readonly onAccept: (annotation: NarrativeAnnotation) => void;
  readonly onDismiss: (annotation: NarrativeAnnotation) => void;
}

function SuggestionPanel({
  annotations,
  loading,
  onAccept,
  onDismiss,
}: SuggestionPanelProps): ReactElement {
  const { t } = useTranslation();

  if (annotations.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
        {loading
          ? t('patient.medicalHistory.suggestions.scanning')
          : t('patient.medicalHistory.suggestions.empty')}
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {annotations.map((a) => (
        <li
          key={`${a.termId}-${a.start.toString()}`}
          className="rounded-md border border-border/60 bg-card px-2.5 py-2 space-y-1.5"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-card-foreground truncate">
              {a.label}
            </span>
            <span
              className={cn(
                'text-[9px] font-mono uppercase px-1.5 py-0.5 rounded',
                a.vocabulary === 'hpo'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-amber-500/15 text-amber-600',
              )}
            >
              {a.vocabulary}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground italic line-clamp-2">
            “{a.snippet}”
          </p>
          <div className="flex justify-end gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={() => {
                onDismiss(a);
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <X className="h-3 w-3" aria-hidden="true" />
              {t('patient.medicalHistory.suggestions.dismiss')}
            </button>
            <button
              type="button"
              onClick={() => {
                onAccept(a);
              }}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Check className="h-3 w-3" aria-hidden="true" />
              {t('patient.medicalHistory.suggestions.accept')}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
